import { findSpellById } from '@/data/spells';
import type { MoveChoice } from '@/schemas/move';
import type { Spell } from '@/schemas/spell';
import { isSpellAccessAvailable } from '@/services/spellAccess';

/**
 * Helper SSOT per il rendering "spell-aware" delle `MoveChoice`
 * (mago.archetipo-umano, chierico.archetipo-umano e simili). Estratti in
 * file dedicato per rispettare `react-refresh/only-export-components` su
 * `SpellChoiceCard.tsx` (che esporta solo il componente).
 */

/**
 * **FI-79 (2026-05-23)** — Map `optionId → Spell` per le option di un
 * gruppo che referenziano spell del catalogo. Heuristic data-driven: la
 * Map è popolata solo se *tutte* le option del gruppo sono spell del
 * catalogo. Ritorna `null` se almeno una option non è una spell —
 * sentinel per fallback al rendering classico label+radio/checkbox
 * (mosse cross-class, Tradizioni, ecc.).
 */
export function buildSpellLookup(options: readonly MoveChoice[]): Map<string, Spell> | null {
  const lookup = new Map<string, Spell>();
  for (const opt of options) {
    const spell = findSpellById(opt.id);
    if (!spell) return null;
    lookup.set(opt.id, spell);
  }
  return lookup.size > 0 ? lookup : null;
}

/**
 * **FI-79 (2026-05-23)** — `true` se la spell ha almeno una entry
 * `availableTo` soddisfatta dai mosse/archetipo del PG. Returns `false`
 * per spell con `requiresMove` non soddisfatto (es. `curare-ferite-moderate`
 * richiede `chierico.primo-soccorso-migliorato`, mossa L6-10). Il
 * consumer UI marca l'option come `disabled`.
 */
export function isSpellChoiceAccessible(
  spell: Spell,
  archetypeId: string | undefined,
  ownedMoveIds: readonly string[],
): boolean {
  return spell.availableTo.some((access) =>
    isSpellAccessAvailable(access, { archetypeId, ownedMoveIds }),
  );
}
